import React, { useState, useEffect } from 'react'
import { Search, Plus, Edit, Trash2, RefreshCw, ChevronDown, CheckCircle, XCircle, AlertCircle, X, Filter } from 'lucide-react'
import { jobService } from '../../services/jobService'
import { employeeService } from '../../services/employeeService'
import InlineLoader from '../../components/ui/InlineLoader'
import CreateJobModal from '../../components/modals/CreateJobModal'
import EditJobModal from '../../components/modals/EditJobModal'
import DeleteJobModal from '../../components/modals/DeleteJobModal'
import { format } from 'date-fns'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-2 ${
    type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
    <span>{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2">
      <X className="h-4 w-4" />
    </button>
  </div>
)

const JobManagement = () => {
  const [jobs, setJobs] = useState([])
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedJob, setSelectedJob] = useState(null)
  const [toastMessage, setToastMessage] = useState(null)

  // Helper function to safely get property value
  const safeGet = (obj, path, defaultValue = '') => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToastMessage({ message, type })
    setTimeout(() => setToastMessage(null), 3000)
  }

  const fetchJobs = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await jobService.getAllJobs()
      if (response.success) {
        setJobs(response.data.jobs || [])
      } else {
        setError('Failed to fetch jobs')
      }
    } catch (error) {
      console.error('Error fetching jobs:', error)
      setError(error.message || 'Failed to load jobs')
      setJobs([])
    } finally {
      setLoading(false)
    }
  }

  const fetchEmployees = async () => {
    try {
      const response = await employeeService.getAllEmployees()
      if (response.success) {
        setEmployees(response.data.employees || [])
      }
    } catch (error) {
      console.error('Error fetching employees:', error)
      setEmployees([])
    }
  }

  // Helper function to count employees for a specific job
  const getEmployeeCountForJob = (jobId) => {
    return employees.filter(employee => {
      const employeeJobId = employee.job?._id || employee.job
      return employeeJobId === jobId
    }).length
  }

  // Fetch both jobs and employees on component mount
  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([fetchJobs(), fetchEmployees()])
    }
    fetchData()
  }, [])

  // Refresh all data
  const refreshData = async () => {
    await Promise.all([fetchJobs(), fetchEmployees()])
  }

  const handleCreateJob = async (jobData) => {
    try {
      const response = await jobService.createJob(jobData)
      if (response.success) {
        showToast('Job created successfully')
        fetchJobs()
        setShowCreateModal(false)
      } else {
        showToast(response.message || 'Failed to create job', 'error')
      }
    } catch (error) {
      console.error('Error creating job:', error)
      showToast('Error creating job', 'error')
    }
  }

  const handleEditJob = async (jobData) => {
    try {
      const response = await jobService.updateJob(selectedJob._id, jobData)
      if (response.success) {
        showToast('Job updated successfully')
        fetchJobs()
        setShowEditModal(false)
        setSelectedJob(null)
      } else {
        showToast(response.message || 'Failed to update job', 'error')
      }
    } catch (error) {
      console.error('Error updating job:', error)
      showToast('Error updating job', 'error')
    }
  }

  const handleDeleteJob = async () => {
    try {
      const response = await jobService.deleteJob(selectedJob._id)
      
      if (response.success) {
        showToast('Job deleted successfully')
        fetchJobs()
        setShowDeleteModal(false)
        setSelectedJob(null)
      } else {
        showToast(response.message || 'Failed to delete job', 'error')
      }
    } catch (error) {
      console.error('Error deleting job:', error)
      showToast('Error deleting job', 'error')
    }
  }

  const openEditModal = (job) => {
    setSelectedJob(job)
    setShowEditModal(true)
  }

  const openDeleteModal = (job) => {
    setSelectedJob(job)
    setShowDeleteModal(true)
  }

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.name.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && job.isActive) ||
      (statusFilter === 'inactive' && !job.isActive)
    
    return matchesSearch && matchesStatus
  })

  // Loading state
  if (loading && jobs.length === 0) {
    return (
      <InlineLoader text="Loading jobs..." />
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Job Management</h1>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={refreshData}
                className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Refresh</span>
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Create Job</span>
              </button>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-12">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading jobs</h3>
              <p className="text-gray-500 mb-4">{error}</p>
              <button
                onClick={refreshData}
                className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors mx-auto"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Try Again</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Toast Notification */}
        {toastMessage && (
          <Toast
            message={toastMessage.message}
            type={toastMessage.type}
            onClose={() => setToastMessage(null)}
          />
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Job Management</h1>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={refreshData}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Create Job</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filter Jobs</h2>
            </div>
            {(searchTerm || statusFilter !== 'all') && (
              <button
                onClick={() => {
                  setSearchTerm('')
                  setStatusFilter('all')
                }}
                className="px-4 py-2 text-orange-600 border border-orange-600 rounded-lg hover:bg-orange-50 transition-colors"
              >
                Clear Filters
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search job name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 w-full border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none pr-10 bg-white"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Results count */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredJobs.length} of {jobs.length} jobs
          </div>
        </div>

        {/* Jobs Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    No of Employees
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Create At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Updated At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredJobs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                      {searchTerm || statusFilter !== 'all' ? 'No jobs found matching your criteria' : 'No jobs found'}
                    </td>
                  </tr>
                ) : (
                  filteredJobs.map((job) => (
                    <tr key={job._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {job.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {safeGet(job, 'createdBy.username', 'N/A')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {getEmployeeCountForJob(job._id)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {job.createdAt ? format(new Date(job.createdAt), 'yyyy-MM-dd') : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {job.updatedAt ? format(new Date(job.updatedAt), 'yyyy-MM-dd') : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            job.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {job.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openEditModal(job)}
                            className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                            title="Edit Job"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(job)}
                            className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                            title="Delete Job"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modals */}
        <CreateJobModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateJob}
        />

        <EditJobModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false)
            setSelectedJob(null)
          }}
          onSubmit={handleEditJob}
          job={selectedJob}
        />

        <DeleteJobModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false)
            setSelectedJob(null)
          }}
          onConfirm={handleDeleteJob}
          job={selectedJob}
        />
      </div>
    </div>
  )
}

export default JobManagement
